import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { message } from 'antd';
import { messageI18n } from '../../i18n/toastI18n';
import { AxiosError } from 'axios';
import { UnifiedUploadParams, uploadUnifiedCaseSweeps } from './unified';

export interface UnifiedUploadSweepsResult {
  data: { upload_session_id: string; study_instance_uid: string; status: string; missing_keys: string[] };
  failed: { key: string; reason: string }[];
  successKeys: string[];
}

export const useUnifiedUpload = (
  options?: Omit<
    UseMutationOptions<
      UnifiedUploadSweepsResult,
      AxiosError,
      UnifiedUploadParams
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  const { onSuccess, onError, ...restOptions } = options || {};

  return useMutation<
    UnifiedUploadSweepsResult,
    AxiosError,
    UnifiedUploadParams
  >({
    mutationKey: ['uploadUnifiedCase'],
    mutationFn: uploadUnifiedCaseSweeps,

    ...restOptions,

    onSuccess: (data, variables, context) => {
      if (data.failed.length > 0) {
        const firstFailure = data.failed[0]?.reason;
        messageI18n.warning(
          firstFailure
            ? 'upload.caseCreatedIncompleteWithReason'
            : 'upload.caseCreatedIncomplete',
          {
            uploaded: data.successKeys.length,
            failed: data.failed.length,
            reason: firstFailure,
          }
        );
      } else {
        messageI18n.success('upload.caseCreated');
      }

      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      queryClient.invalidateQueries({ queryKey: ['paginated-exams'] });
      queryClient.invalidateQueries({ queryKey: ['pregnancy', 'get-history'] });
      queryClient.invalidateQueries({ queryKey: ['recent-studies'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });

      const uploadedStudyUid = variables.study_instance_uid;
      if (uploadedStudyUid) {
        queryClient.invalidateQueries({
          queryKey: [`studyInfo/${uploadedStudyUid}`],
        });
        queryClient.invalidateQueries({
          queryKey: [`globalReportData/${uploadedStudyUid}`],
        });
        queryClient.invalidateQueries({
          queryKey: [`viewImages/${uploadedStudyUid}`],
        });
        queryClient.invalidateQueries({
          queryKey: ['general-viewer-instance-frames', uploadedStudyUid],
        });
      }

      console.log('[useUnifiedUpload] Cache invalidated.');

      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },

    onError: (error, variables, context) => {
      let errorMessage = 'An unexpected error occurred during the upload.';
      const candidate = error as AxiosError<{
        detail?: string | Array<{ msg?: string }>;
        message?: string;
        status_code?: number;
      }> & {
        detail?: string | Array<{ msg?: string }>;
        message?: string;
        status_code?: number;
      };
      const responseData = candidate.response?.data;
      const detailVal = responseData?.detail ?? candidate.detail;
      if (typeof detailVal === 'string') {
        errorMessage = detailVal;
      } else if (Array.isArray(detailVal)) {
        errorMessage = `Validation Error: ${detailVal[0]?.msg || 'Check inputs'}`;
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      } else if (candidate.message) {
        errorMessage = candidate.message;
      }

      message.error(errorMessage);

      if (onError) {
        onError(error, variables, context);
      }
    },
  });
};
